import { types } from "mobx-state-tree";
import fetcher from "../utils/fetcher";

/**
 * Store for the "Choosing Tenants" (בחירת דיירים) feature. Wraps the ERP
 * (Priority) endpoints proxied by the coral backend under /tenants:
 *   - get/add_tenant_apartment_plan  → ZCOC_APARTMENTPLANS_SUBFORM (Plans tab)
 *   - get_tenant_selection_types       → ZCOC_TENANTSELTYPE ("סוג בחירת דייר")
 *   - get/add/update_tenant_selections → ZCOC_TENANTSELECTION_SUBFORM (rows)
 *   - get_status_enter_by_tenant       → ZCOC_TENANTSELSTAT (statuses)
 *   - get_tenant_sup_selection          → ZCOC_SUPCHOOSE (suppliers) [route TBD]
 *
 * Actions are thin pass-throughs: callers build the request body from
 * tenantsStore.selectedSiteSpace, e.g.
 *   { CUST, MAINENV, SPACENAME }                      for reads
 *   { CUST, MAINENV, SPACENAME, data: {...} }         for add/update
 */
const TenantsChooseStore = types
  .model("TenantsChooseStore", {
    tenantSpace: types.frozen(),
    selections: types.frozen(),
    changes: types.frozen(),
    selectionTypes: types.frozen(),
    apartmentPlans: types.frozen(),
    enteredStatuses: types.frozen(),
    suppliers: types.frozen(),
    error: types.frozen(),
  })
  .actions((self) => {
    function setError(error, response) {
      self.error =
        response?.data?.FORM?.InterfaceErrors?.text?.replace(
          /^.*?\d+\s*-\s*/,
          ""
        ) || error;
    }

    const setTenantSpace = (value) => {
      self.tenantSpace = value;
    };
    const setSelections = (value) => {
      self.selections = value;
    };
    const setChanges = (value) => {
      self.changes = value;
    };
    const setSelectionTypes = (value) => {
      self.selectionTypes = value;
    };
    const setApartmentPlans = (value) => {
      self.apartmentPlans = value;
    };
    const setEnteredStatuses = (value) => {
      self.enteredStatuses = value;
    };
    const setSuppliers = (value) => {
      self.suppliers = value;
    };

    // ---- Selections (table rows) -----------------------------------------
    const getSelections = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.choose.get_selections();
      if (response?.status === 200 && response?.data) {
        self.setSelections(response.data);
        return response.data;
      }
      self.setError("error:error_failed_get_tenant_selections_req");
    };

    // ---- Changes (ZCOC_TENENTCHANGE) -------------------------------------
    const getChanges = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.choose.get_changes();
      if (response?.status === 200 && response?.data) {
        self.setChanges(response.data);
        return response.data;
      }
      self.setError("error:error_failed_get_tenant_changes_req");
    };

    const addChange = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.choose.add_change();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_add_tenant_change_req");
    };

    const updateChange = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.choose.update_change();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_update_tenant_change_req");
    };

    const addSelection = async (data) => {
      self.setError("");
      const response = await fetcher().post(data).from.choose.add_selection();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_add_tenant_selection_req");
    };

    const updateSelection = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.update_selection();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_update_tenant_selection_req");
    };

    // ---- Selection types ("סוג בחירת דייר") -------------------------------
    const getSelectionTypes = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_selection_types();
      if (response?.status === 200 && response?.data) {
        self.setSelectionTypes(response.data);
        return response.data;
      }
      self.setError("error:error_failed_get_tenant_selection_types_req");
    };

    // ---- Tenant space (source of SITE / BUILDINGNUM / SPACETYPECODE) --------
    const getTenantSpace = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_tenant_space();
      if (response?.status === 200) {
        self.setTenantSpace(response.data || null);
        return response.data;
      }
      self.setError("error:error_failed_get_tenant_case_req");
    };

    // Save the signed price-quote PDF (add_signed_edqout):
    //   { CUST, MAINENV, SPACENAME, data: { EXTFILENAME: signedBase64 } }
    const addSignedEdqout = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.add_signed_edqout();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_add_signed_edqout_req");
    };

    // Read the signed price-quote PDF (signed_edqout).
    const getSignedEdqout = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.signed_edqout();
      if (response?.status === 200) {
        return response.data?.value || response.data;
      }
      self.setError("error:error_failed_get_signed_edqout_req");
    };

    // ---- Apartment plans (Plans tab + New Plan form) ----------------------
    const getApartmentPlans = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_apartment_plans();
      if (response?.status === 200 && response?.data) {
        self.setApartmentPlans(response.data);
        return response.data;
      }
      self.setError("error:error_failed_get_tenant_apartment_plans_req");
    };

    const addApartmentPlan = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.add_apartment_plan();
      if (response?.status === 200) {
        return response.data;
      }
      self.setError("error:error_failed_add_tenant_apartment_plan_req");
    };

    // ---- Statuses entered by tenant --------------------------------------
    const getEnteredStatuses = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_entered_statuses();
      if (response?.status === 200 && response?.data) {
        // ERP returns { value: [...] } for this one
        const value = response.data?.value || response.data;
        self.setEnteredStatuses(value);
        return value;
      }
      self.setError("error:error_failed_get_status_enter_by_tenant_req");
    };

    // ---- Suppliers ("שם ספק נבחר") — backend route pending ----------------
    const getSupplierSelection = async (data) => {
      self.setError("");
      const response = await fetcher()
        .post(data)
        .from.choose.get_supplier_selection();
      if (response?.status === 200 && response?.data) {
        const value = response.data?.value || response.data;
        self.setSuppliers(value);
        return value;
      }
      self.setError("error:error_failed_get_tenant_sup_selection_req");
    };

    return {
      setError,
      setTenantSpace,
      getTenantSpace,
      addSignedEdqout,
      getSignedEdqout,
      setSelections,
      setChanges,
      getChanges,
      addChange,
      updateChange,
      setSelectionTypes,
      setApartmentPlans,
      setEnteredStatuses,
      setSuppliers,
      getSelections,
      addSelection,
      updateSelection,
      getSelectionTypes,
      getApartmentPlans,
      addApartmentPlan,
      getEnteredStatuses,
      getSupplierSelection,
    };
  })
  .views((self) => ({
    get isError() {
      return !!self.error;
    },
  }));

const tenantsChooseStore = TenantsChooseStore.create({
  tenantSpace: null,
  selections: [],
  changes: [],
  selectionTypes: [],
  apartmentPlans: [],
  enteredStatuses: [],
  suppliers: [],
  error: "",
});

export default tenantsChooseStore;
